import { useState, useEffect } from "react";
import { Button, Alert } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers-pro";
import dayjs from "dayjs";
import BillingDataService from "../services/billing.service";
import FileSaver from "file-saver";
import { useQuery } from "@apollo/client";
import { getRelationship } from "../queries";

interface InvoiceExportComponentProps {
  relationshipId: string;
}

export function InvoiceExportComponent({ relationshipId }: InvoiceExportComponentProps) {
  const [selectedServicePeriod, setSelectedServicePeriod] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  // Reset date picker when relationship changes
  useEffect(() => {
    setSelectedServicePeriod(null);
    setError(null);
  }, [relationshipId]);

  // Fetch relationship data
  const { data: relationshipData } = useQuery(getRelationship, {
    variables: { id: relationshipId },
  });

  const relationshipName = relationshipData?.getRelationship?.name;
  const earliestLiveDate = relationshipData?.getRelationship?.earliestLiveDate
    ? dayjs(relationshipData.getRelationship.earliestLiveDate).startOf("month")
    : dayjs("2021-01-01");
  // Max date is the end of the previous month (to make it inclusive for month picker)
  const maxDate = dayjs().subtract(1, "month").endOf("month");

  const download = async () => {
    try {
      setError(null);
      const servicePeriod =
        selectedServicePeriod && dayjs(selectedServicePeriod).isValid() ? dayjs(selectedServicePeriod).format("YYYY-MM-DD") : undefined;
      const response = await BillingDataService.enterpriseInvoiceExport(parseInt(relationshipId), servicePeriod);
      const blob = new Blob([response.data.output], { type: "text/csv;charset=utf-8" });
      FileSaver.saveAs(
        blob,
        `${relationshipName || relationshipId} ${
          servicePeriod && dayjs(servicePeriod).isValid() ? dayjs(servicePeriod).format("MMM-YYYY") : "All-Time"
        } Invoice Summary.csv`,
      );
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || "Failed to download invoice export. Please try again.");
    }
  };

  return (
    <>
      <div>
        <DatePicker
          key={relationshipId}
          label="Billing Period (Optional)"
          value={selectedServicePeriod}
          views={["year", "month"]}
          onChange={(newDate) => {
            if (newDate) {
              setSelectedServicePeriod(newDate.startOf("month"));
            } else {
              setSelectedServicePeriod(null);
            }
          }}
          minDate={earliestLiveDate}
          maxDate={maxDate}
          slotProps={{
            field: { clearable: true },
          }}
        />
      </div>
      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}
      <div>
        <Button variant="contained" onClick={download}>
          Download
        </Button>
      </div>
    </>
  );
}
